import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Constants } from "../utils/Constants";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../store/ducks/auth.duck";
import { logout } from "../crud/auth.crud";
import "./ImpersonationBanner.css";

const baseUrlImpersonation = `${Constants.url}/api/auth/impersonation/tenant`;

const clearImpersonationData = () => {
  localStorage.removeItem("impersonation_active");
  localStorage.removeItem("impersonation_session_id");
  localStorage.removeItem("impersonation_expires_at");
  localStorage.removeItem("impersonation_requester");
  localStorage.removeItem("impersonation_approver");
  localStorage.removeItem("impersonation_merchant");
};

const ImpersonationBanner = () => {
  const [sessionData, setSessionData] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [endingSession, setEndingSession] = useState(false);
  const dispatch = useDispatch();
  const abortControllerRef = useRef(null);
  const me = useSelector((state) => state.auth.me);

  const isImpersonating = me?.impersonation != null;

  const handleSessionTerminated = useCallback(
    async (reason = "terminated") => {
      if (!isImpersonating) {
        return;
      }

      clearImpersonationData();
      setSessionData(null);
      setRemainingTime(0);

      if (reason === "terminated" || reason === "connection_error") {
        return;
      }

      try {
        await logout();
      } catch {
        // Ignora erro - sessão pode já estar inválida
      }

      dispatch(actions.logout());

      const messages = {
        expired: {
          title: "Sessão Expirada",
          text: "A sessão de impersonificação expirou. Você será redirecionado para o painel Admin.",
        },
        manual: {
          title: "Sessão Encerrada",
          text: "A sessão de impersonificação foi encerrada manualmente pelo administrador. Você será redirecionado para o painel Admin.",
        },
      };

      const message = messages[reason] || messages.expired;

      Swal.fire({
        title: message.title,
        text: message.text,
        icon: "warning",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      }).then(() => {
        window.location.href = Constants.adminUrl;
      });
    },
    [dispatch, isImpersonating]
  );

  const checkSession = useCallback(async () => {
    if (!isImpersonating) {
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const response = await axios.get(`${baseUrlImpersonation}/current`, {
        timeout: 10000,
        signal: abortControllerRef.current.signal,
      });

      if (!response.data) {
        handleSessionTerminated("terminated");
        return;
      }

      setSessionData(response.data);
      setRemainingTime(response.data.remaining_seconds || 0);
    } catch (error) {
      if (axios.isCancel(error) || error.name === "AbortError") {
        return;
      }

      const status = error.response?.status;
      const endReason = error.response?.data?.end_reason;

      const reasonMap = {
        expired: "expired",
        manual: "manual",
      };

      const terminationReason =
        reasonMap[endReason] ||
        (status === 404 ? "terminated" : "connection_error");

      handleSessionTerminated(terminationReason);
    } finally {
      setLoading(false);
    }
  }, [handleSessionTerminated, isImpersonating]);

  useEffect(() => {
    checkSession();
    const interval = setInterval(checkSession, 30000);

    return () => {
      clearInterval(interval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [checkSession]);

  useEffect(() => {
    if (sessionData?.active) {
      document.body.classList.add("has-impersonation-banner");
    } else {
      document.body.classList.remove("has-impersonation-banner");
    }

    return () => {
      document.body.classList.remove("has-impersonation-banner");
    };
  }, [sessionData]);

  useEffect(() => {
    if (remainingTime <= 0) return;

    const countdown = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          handleSessionTerminated("expired");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdown);
  }, [remainingTime, handleSessionTerminated]);

  const handleEndSession = async () => {
    if (!sessionData || endingSession) return;

    const result = await Swal.fire({
      title: "Encerrar Sessão",
      text: "Tem certeza que deseja encerrar esta sessão?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, encerrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    setEndingSession(true);

    try {
      await axios.post(`${baseUrlImpersonation}/end`, null, { timeout: 10000 });

      clearImpersonationData();

      dispatch(actions.logout());

      Swal.fire({
        title: "Sessão Encerrada",
        text:
          "Sessão encerrada com sucesso. Você será redirecionado para o painel Admin.",
        icon: "success",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      }).then(() => {
        window.location.href = Constants.adminUrl;
      });
    } catch (error) {
      console.error("Error ending session:", error);
      setEndingSession(false);
      Swal.fire({
        title: "Erro",
        text: "Erro ao encerrar sessão. Tente novamente.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  if (loading || !isImpersonating || !sessionData || !sessionData.active) {
    return null;
  }

  const isLowTime = remainingTime < 300;
  const isSelfApproved =
    sessionData.requester?.id != null &&
    sessionData.requester?.id === sessionData.approver?.id;
  const accessText = isSelfApproved
    ? "como Super Admin"
    : "como suporte autorizado";

  return (
    <div className={`impersonation-banner ${isLowTime ? "warning" : ""}`}>
      <div className="impersonation-content">
        <div className="impersonation-info">
          <span className="impersonation-text">
            Você está acessando a conta de{" "}
            <strong>
              {sessionData.merchant?.emp_nome_fantasia ||
                sessionData.merchant?.emp_razao_social}
            </strong>{" "}
            {accessText}.
          </span>
          <span className="impersonation-time">
            Sessão expira em <strong>{formatTime(remainingTime)}</strong>
          </span>
        </div>
        <button
          className="impersonation-end-button"
          onClick={handleEndSession}
          disabled={endingSession}
        >
          {endingSession ? "Encerrando..." : "Encerrar sessão"}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
